import React, { useEffect, useState } from "react";
import "./style.scss";
import ReactFlagsSelect from "react-flags-select";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { State } from "../../app/store";
import { GET_COUNTRIES } from "../../app/api/apiRoutes";
import { Country } from "../../app/models/Country";
import useSilentApiRequest from "../../utils/hooks/useSilentApiRequest";

interface IProps {
  isMobile?: boolean;
}

const LanguageSelect: React.FC<IProps> = ({ isMobile = false }) => {
  const { get } = useSilentApiRequest();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const facilityIds = useSelector((state: State) => state.config.facilityIds);
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(i18n.language);
  const [availableCountries, setAvailableCountries] = useState<string[]>(["PL"]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const langParam = searchParams.get("lang");
    switch (langParam?.toLocaleLowerCase()) {
      case "pl":
        i18n.changeLanguage("PL");
        break;
      case "en":
        i18n.changeLanguage("GB");
        break;
      case "de":
        i18n.changeLanguage("DE");
        break;
      case "ua":
        i18n.changeLanguage("UA");
        break;
      default:
        return;
    }
    //eslint-disable-next-line
  }, [searchParams]);

  const fetchCountries = async () => {
    if (!facilityIds) return;

    setIsLoadingCountries(true);

    const { result, isError } = await get(GET_COUNTRIES(facilityIds));
    if (!isError) {
      const countries = result || [];
      const supportedCountries = ["PL", "GB", "DE", "UA"];
      const availableCountryCodes: string[] = ["PL"]; // PL jest zawsze dostępne

      countries.forEach((country: Country) => {
        const alphaCode2 = country.alphaCode2;
        if (supportedCountries.includes(alphaCode2) && alphaCode2 !== "PL") {
          availableCountryCodes.push(alphaCode2);
        }
      });
      setAvailableCountries(availableCountryCodes);
    } else {
      setAvailableCountries(["PL", "GB", "DE", "UA"]);
    }
    setIsLoadingCountries(false);
  };

  useEffect(() => {
    fetchCountries();
    //eslint-disable-next-line
  }, []);

  const customLabels = {
    PL: { primary: "PL" },
    GB: { primary: "EN" },
    DE: { primary: "DE" },
    UA: { primary: "UA" },
  };

  const onChangeLanguage = (value: string) => {
    i18n.changeLanguage(value);
    window.location.reload();
  };

  return (
    <>
      {saleConfiguration.languageSelectActive && (
        <>
          {isLoadingCountries ? (
            <div className="language-select-loading">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <>
              {!isMobile ? (
                <div className={"language-select-wrapper"}>
                  <ReactFlagsSelect
                    selected={language}
                    onSelect={(value: string) => onChangeLanguage(value)}
                    countries={availableCountries}
                    customLabels={customLabels}
                    className="language-select"
                    selectButtonClassName="language-select__button"
                  />
                </div>
              ) : (
                <ReactFlagsSelect
                  selected={language}
                  onSelect={(value: string) => onChangeLanguage(value)}
                  countries={availableCountries}
                  customLabels={customLabels}
                  className="react-flags-select-mobile language-select"
                  selectButtonClassName="language-select__button"
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default LanguageSelect;
